import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import {
  Leaf,
  AudioLines,
  ChartNoAxesColumnIncreasing,
  Clock,
} from 'lucide-react';
import { useAppState } from 'src/state-management/state-provider';
import MostFrequentSpeciesCard from './speciesCatalog/MostFrequentSpeciesCard';
import SpeciesSpreadClassCard, { SPREAD_CLASSES } from './speciesCatalog/SpeciesSpreadClassCard';
import SpeciesCard from './speciesCatalog/SpeciesCard';
import SpeciesPatternDialog from './speciesCatalog/SpeciesPatternDialog';
import { isTataProject } from './speciesCatalog/projectAccess';
import {
  BUSIEST_SLOT,
  COMBINED_DAILY_OCCURRENCE,
  DEPLOYMENT_COUNT,
  DEPLOYMENTS_WITH_ACTIVITY_DATA,
  NO_PEAK_COUNT,
  PEAK_BANDS,
  SPECIES_CATALOG,
  SpeciesCatalogEntry,
} from './speciesCatalog/data/catalogData';

interface SpeciesCatalogProps {
  projectName?: string;
  deploymentId?: string;
  siteName?: string;
  deploymentName?: string;
  deploymentsList?: Array<{
    deployment_id: string;
    deployment_name: string;
    site_name?: string;
    [key: string]: unknown;
  }>;
  onDeploymentSelect?: (id: string, name: string, siteName?: string) => void;
  summary?: {
    totalSpecies: number;
    totalDetections: number;
    filesProcessed: number;
    filesTotal: number;
  };
  onUploadClick?: () => void;
}

type OccurrenceSort = 'highest' | 'lowest';
/** 'all', 'none' (no peak recorded) or a peak band name from the data, e.g. 'Dawn'. */
type PeakFilter = string;

const topSpecies = SPECIES_CATALOG.find((s) => s.dailyOccurrence !== null);

const CATALOG_STAT_CHIPS = [
  {
    heading: 'SPECIES CATALOGUED',
    value: String(SPECIES_CATALOG.length),
    content: `High-confidence species across ${DEPLOYMENT_COUNT} deployments`,
    icon: Leaf,
  },
  {
    heading: 'COMBINED AVG. DAILY OCCURRENCE',
    value: COMBINED_DAILY_OCCURRENCE.toLocaleString('en-US'),
    content: `Summed across ${DEPLOYMENTS_WITH_ACTIVITY_DATA} of ${DEPLOYMENT_COUNT} deployments with activity data`,
    icon: AudioLines,
  },
  {
    heading: 'MOST FREQUENT SPECIES',
    value: (topSpecies?.dailyOccurrence ?? 0).toLocaleString('en-US'),
    content: topSpecies
      ? `${topSpecies.name} · ${Math.round((topSpecies.share ?? 0) * 100)}% of the combined daily occurrence`
      : 'No activity data',
    icon: ChartNoAxesColumnIncreasing,
  },
  {
    heading: 'BUSIEST TIME OF DAY',
    value: BUSIEST_SLOT.start,
    valueSuffix: `to ${BUSIEST_SLOT.end}`,
    content: 'Highest combined avg. occurrence in a 30-minute slot',
    icon: Clock,
  },
];

const FILTER_CONTROL_SX = {
  height: '36px',
  minWidth: '210px',
  bgcolor: 'rgba(255, 255, 255, 0.02)',
  borderRadius: '8px',
  border: '1px solid #222',
  color: '#fff',
  fontSize: '12px',
  '& fieldset': { border: 'none' },
  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.04)' },
  '& .MuiSelect-icon': { color: 'rgba(255, 255, 255, 0.4)' },
};

const MENU_PROPS = {
  PaperProps: {
    sx: {
      bgcolor: '#0F1412',
      border: '1px solid #1E2E25',
      color: '#EDEDED',
      '& .MuiMenuItem-root': { fontSize: '12px' },
      '& .MuiMenuItem-root.Mui-selected': { bgcolor: 'rgba(16, 185, 129, 0.15)' },
      '& .MuiMenuItem-root:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' },
    },
  },
};

const MenuLabel: React.FC<{ label: string; note?: string }> = ({ label, note }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
    <span>{label}</span>
    {note && <span style={{ color: '#6B7280', fontVariantNumeric: 'tabular-nums' }}>{note}</span>}
  </Box>
);

/** Highest/lowest by daily occurrence; species without activity data always sit at the end. */
const sortByOccurrence = (list: SpeciesCatalogEntry[], order: OccurrenceSort) =>
  [...list].sort((a, b) => {
    if (a.dailyOccurrence === null || b.dailyOccurrence === null) {
      if (a.dailyOccurrence === b.dailyOccurrence) return a.name.localeCompare(b.name);
      return a.dailyOccurrence === null ? 1 : -1;
    }
    const diff = a.dailyOccurrence - b.dailyOccurrence;
    return (order === 'highest' ? -diff : diff) || a.name.localeCompare(b.name);
  });

const SpeciesCatalog: React.FC<SpeciesCatalogProps> = ({
  projectName,
  siteName,
  deploymentName,
}) => {
  const { state } = useAppState();
  const [searchTerm, setSearchTerm] = useState('');
  const [spreadClassFilter, setSpreadClassFilter] = useState<string | null>(null);
  const [occurrenceSort, setOccurrenceSort] = useState<OccurrenceSort>('highest');
  const [peakFilter, setPeakFilter] = useState<PeakFilter>('all');
  const [detailSpecies, setDetailSpecies] = useState<SpeciesCatalogEntry | null>(null);

  const isTata = isTataProject(
    projectName,
    state.selectedProject?.projectDetails?.project_name,
    siteName,
    deploymentName,
  );

  const filteredSpecies = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    const matches = SPECIES_CATALOG.filter((sp) => {
      if (query && !sp.name.toLowerCase().includes(query)) return false;
      if (spreadClassFilter && sp.spreadClass !== spreadClassFilter) return false;
      if (peakFilter === 'none') return !sp.peakBand;
      if (peakFilter !== 'all' && sp.peakBand !== peakFilter) return false;
      return true;
    });
    return sortByOccurrence(matches, occurrenceSort);
  }, [searchTerm, spreadClassFilter, peakFilter, occurrenceSort]);

  const hasActiveFilters = Boolean(searchTerm.trim() || spreadClassFilter || peakFilter !== 'all');

  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setSpreadClassFilter(null);
    setPeakFilter('all');
  }, []);

  const handleOpenDetails = useCallback((sp: SpeciesCatalogEntry) => setDetailSpecies(sp), []);

  // Catalogue data is Tata-specific; render nothing for any other project.
  if (!isTata) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%', pb: 4 }}>
      {/* Headline metrics */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 2 }}>
        {CATALOG_STAT_CHIPS.map((chip) => {
          const IconComp = chip.icon;
          return (
            <Box
              key={chip.heading}
              sx={{
                background: 'linear-gradient(180deg, rgba(18, 24, 21, 0.85) 0%, rgba(10, 14, 12, 0.95) 100%)',
                border: '1px solid #1E2E25',
                borderRadius: '12px',
                p: '18px 20px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: '140px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'rgba(16, 185, 129, 0.4)',
                },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: 700,
                    letterSpacing: '0.8px',
                    color: '#9CA3AF',
                    textTransform: 'uppercase',
                    lineHeight: 1.3,
                    pr: 1,
                  }}
                >
                  {chip.heading}
                </Typography>
                <Box sx={{ color: '#10B981', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  <IconComp size={20} strokeWidth={1.8} />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, my: 1.25 }}>
                <Typography
                  sx={{
                    fontSize: '30px',
                    fontWeight: 700,
                    color: '#FFFFFF',
                    lineHeight: 1,
                    letterSpacing: '-0.5px',
                  }}
                >
                  {chip.value}
                </Typography>
                {chip.valueSuffix && (
                  <Typography sx={{ fontSize: '18px', fontWeight: 400, color: '#9CA3AF', lineHeight: 1 }}>
                    {chip.valueSuffix}
                  </Typography>
                )}
              </Box>

              <Typography sx={{ fontSize: '11.5px', color: '#6B7280', lineHeight: 1.35, mt: 'auto' }}>
                {chip.content}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Occurrence & distribution charts (60/40) */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '6fr 4fr' }, gap: 3 }}>
        <MostFrequentSpeciesCard />
        <SpeciesSpreadClassCard
          selectedSpreadClass={spreadClassFilter}
          onSelectSpreadClass={setSpreadClassFilter}
        />
      </Box>

      {/* Search & filters */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search species by name..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: '100%', sm: '300px' },
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255, 255, 255, 0.02)',
                borderRadius: '8px',
                border: '1px solid #222',
                color: '#fff',
                fontSize: '12px',
                height: '36px',
                '& fieldset': { border: 'none' },
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.04)' },
              },
            }}
          />

          <Select
            size="small"
            value={occurrenceSort}
            onChange={(e: SelectChangeEvent) => setOccurrenceSort(e.target.value as OccurrenceSort)}
            inputProps={{ 'aria-label': 'Sort by daily occurrence' }}
            MenuProps={MENU_PROPS}
            sx={FILTER_CONTROL_SX}
          >
            <MenuItem value="highest">Highest daily occurrence</MenuItem>
            <MenuItem value="lowest">Lowest daily occurrence</MenuItem>
          </Select>

          <Select
            size="small"
            value={peakFilter}
            onChange={(e: SelectChangeEvent) => setPeakFilter(e.target.value)}
            inputProps={{ 'aria-label': 'Filter by peak time' }}
            MenuProps={MENU_PROPS}
            renderValue={(value) => {
              if (value === 'all') return 'All peak times';
              if (value === 'none') return 'Peak time: none recorded';
              const band = PEAK_BANDS.find((b) => b.band === value);
              return band ? `Peak: ${band.band} · ${band.from}–${band.to}` : value;
            }}
            sx={{ ...FILTER_CONTROL_SX, minWidth: '230px' }}
          >
            <MenuItem value="all">
              <MenuLabel label="All peak times" note={String(SPECIES_CATALOG.length)} />
            </MenuItem>
            {PEAK_BANDS.map((band) => (
              <MenuItem key={band.band} value={band.band}>
                <MenuLabel label={`${band.band} · ${band.from}–${band.to}`} note={String(band.count)} />
              </MenuItem>
            ))}
            <MenuItem value="none">
              <MenuLabel label="No peak recorded" note={String(NO_PEAK_COUNT)} />
            </MenuItem>
          </Select>

          {spreadClassFilter && (
            <Chip
              label={`Spread class: ${SPREAD_CLASSES.find((c) => c.id === spreadClassFilter)?.name ?? spreadClassFilter}`}
              onDelete={() => setSpreadClassFilter(null)}
              size="small"
              sx={{
                bgcolor: 'rgba(16, 185, 129, 0.15)',
                color: '#10B981',
                border: '1px solid #10B981',
                fontWeight: 700,
                fontSize: '10px',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                '& .MuiChip-deleteIcon': { color: '#10B981', '&:hover': { color: '#fff' } },
              }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography sx={{ fontSize: '12px', color: '#9CA3AF', fontVariantNumeric: 'tabular-nums' }}>
            Showing {filteredSpecies.length} of {SPECIES_CATALOG.length} species
          </Typography>
          {hasActiveFilters && (
            <Typography
              component="button"
              onClick={clearFilters}
              sx={{
                fontSize: '12px',
                color: '#10B981',
                background: 'none',
                border: 'none',
                p: 0,
                cursor: 'pointer',
                textDecoration: 'underline',
                '&:hover': { color: '#fff' },
              }}
            >
              Clear filters
            </Typography>
          )}
        </Box>
      </Box>

      {/* Species cards */}
      {filteredSpecies.length > 0 ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(340px, 1fr))' },
            gap: 2,
          }}
        >
          {filteredSpecies.map((species) => (
            <SpeciesCard key={species.name} species={species} onOpenDetails={handleOpenDetails} />
          ))}
        </Box>
      ) : (
        <Box sx={{ border: '1px dashed #1E2E25', borderRadius: '12px', p: 5, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '13px', color: '#9CA3AF' }}>No species match these filters.</Typography>
        </Box>
      )}

      <SpeciesPatternDialog
        species={detailSpecies}
        onClose={() => setDetailSpecies(null)}
      />
    </Box>
  );
};

export default SpeciesCatalog;
